import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Scissors, Trash2, Save, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("de-CH") : "–");

function RuleRow({ rule, companyId, onChanged }) {
  const [start, setStart] = useState(rule.start_text || "");
  const [end, setEnd] = useState(rule.end_text || "");
  const [busy, setBusy] = useState(false);
  const dirty = start.trim() !== (rule.start_text || "") || end.trim() !== (rule.end_text || "");

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put(`/companies/${companyId}/email-trim-rules/${rule.id}`, { start_text: start.trim(), end_text: end.trim() });
      toast.success("Zuschnitt gespeichert – gilt für die nächsten Mails dieses Absenders");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Zuschnitt-Regel für ${rule.sender} löschen? Künftige Mails werden wieder komplett übernommen.`)) return;
    setBusy(true);
    try {
      await apiClient.delete(`/companies/${companyId}/email-trim-rules/${rule.id}`);
      toast.success("Regel gelöscht");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <li className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid={`trim-rule-${rule.id}`}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-medium text-slate-900 truncate"><Mail className="h-3.5 w-3.5 text-slate-500 shrink-0" /><span data-testid={`trim-rule-sender-${rule.id}`}>{rule.sender}</span></div>
          <div className="text-slate-500 truncate">Betreff-Muster: <span className="italic">«{rule.subject_key || "alle Betreffs"}»</span></div>
        </div>
        <div className="text-right text-slate-500 whitespace-nowrap">
          <div data-testid={`trim-rule-applied-${rule.id}`}>{rule.applied_count || 0}× angewendet</div>
          <div>geändert {fmt(rule.updated_at)}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-center">
        <Input value={start} onChange={(e) => setStart(e.target.value)} placeholder="Beginnt bei …" className="h-8 text-xs" data-testid={`trim-rule-start-${rule.id}`} />
        <Input value={end} onChange={(e) => setEnd(e.target.value)} placeholder="Endet bei … (optional)" className="h-8 text-xs" data-testid={`trim-rule-end-${rule.id}`} />
        <div className="flex gap-1.5 justify-end">
          <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700" disabled={busy || !dirty || !(start.trim() || end.trim())} onClick={save} data-testid={`trim-rule-save-${rule.id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-rose-700 border-rose-200 hover:bg-rose-50" disabled={busy} onClick={remove} data-testid={`trim-rule-delete-${rule.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </li>
  );
}

/** Einstellungen: gelernte E-Mail-Zuschnitte (Nur Rechnungsteil) pro Absender prüfen, anpassen, löschen. */
export function SettingsEmailTrimRulesSection({ companyId }) {
  const [rules, setRules] = useState(null);
  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/email-trim-rules`);
      setRules(r.data);
    } catch { setRules([]); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-5 border border-slate-200 bg-white" id="section-email-trim-rules" data-testid="settings-email-trim-rules-section">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-9 w-9 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center flex-shrink-0">
          <Scissors className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-sm font-medium text-slate-900">E-Mail-Zuschnitte (Nur Rechnungsteil)</h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Gelernt aus dem Posteingang: Bei E-Mail-Belegen ohne Anhang (Apple, Netflix …) wird nur der Teil zwischen «Beginnt bei» und «Endet bei»
            ins PDF übernommen – Weiterleitungskopf und Fusstext fallen weg. Änderungen hier gelten sofort für alle künftigen Mails des Absenders.
          </p>
        </div>
      </div>
      {!companyId ? <p className="text-xs text-slate-500">Bitte zuerst eine Firma wählen.</p>
        : rules === null ? <div className="text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        : rules.length === 0 ? (
          <p className="text-xs text-slate-500 rounded-lg border border-dashed border-slate-200 p-4" data-testid="trim-rules-empty">
            Noch keine Zuschnitte gelernt. Im Posteingang: Beleg-Vorschau → «Original-E-Mail anzeigen» → «Nur Rechnungsteil behalten» mit «Für Absender merken».
          </p>
        ) : (
          <ul className="space-y-2" data-testid="trim-rules-list">
            {rules.map((r) => <RuleRow key={`${r.id}-${r.updated_at}`} rule={r} companyId={companyId} onChanged={load} />)}
          </ul>
        )}
    </Card>
  );
}
